import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useProjectStore } from '../../contexts/ProjectContext';
import { useSettings } from '@/contexts/SettingsContext';
import { formatDateByPreference } from '@/utils/dateFormat';
import { Project } from '../../types/project';
import { getProjectSummary } from '../../api/xerData';
import { PanelLoading } from '../../components/ui/loading';
import { ErrorState } from '../../components/ui/error';

const ProjectDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();
  const { settings } = useSettings();
  const { projects, currentProject, setCurrentProject, refreshProjectSchedules } = useProjectStore();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [projectSummary, setProjectSummary] = useState<any>(null);

  useEffect(() => {
    if (projectId) {
      const project = projects.find(p => p.id === projectId);
      if (project) {
        setCurrentProject(project);
        // Refresh project schedules to get real data
        refreshProjectSchedules(projectId);
        // Fetch project summary from backend
        fetchProjectSummary(projectId);
      } else {
        setError('Project not found');
      }
    }
  }, [projectId, projects, setCurrentProject, refreshProjectSchedules]);

  const fetchProjectSummary = async (id: string) => {
    try {
      const summary = await getProjectSummary(id);
      setProjectSummary(summary);
    } catch (error) {
      console.error('Error fetching project summary:', error);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <PanelLoading message="Loading project..." />
      </div>
    );
  }

  if (error || !currentProject) {
    return (
      <div className="p-6">
        <ErrorState
          title="Error"
          description={error || 'Project not found'}
        />
        <div className="mt-4">
          <Link to="/app/projects" className="text-nb-accent hover:text-nb-accent-hover">
            Return to Projects
          </Link>
        </div>
      </div>
    );
  }

  // Calculate project statistics from real data
  const totalSchedules = currentProject.schedules?.length || 0;
  const currentSchedules = currentProject.schedules?.filter(s => s.status === 'Current').length || 0;
  const baselineSchedules = currentProject.schedules?.filter(s => s.status === 'Baseline').length || 0;
  const totalActivities = currentProject.schedules?.reduce((sum, s) => sum + (s.activity_count || 0), 0) || 0;

  // Use real data from backend when available
  const realTotalActivities = projectSummary?.total_activities || totalActivities;
  const realProgress = projectSummary?.progress || 0;
  const realBudget = projectSummary?.budget || 0;
  const realSpent = projectSummary?.spent || 0;
  const realStartDate = projectSummary?.start_date || currentProject.start_date;
  const realEndDate = projectSummary?.end_date || currentProject.end_date;

  return (
    <div className="p-6">
      {/* Breadcrumb navigation */}
      <div className="mb-4 flex items-center text-sm text-gray-500">
        <Link to="/projects" className="hover:text-primary">Projects</Link>
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mx-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
        <span className="font-medium text-gray-900">{currentProject.name}</span>
      </div>

      {/* Project header */}
      <div className="mb-8">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">{currentProject.name}</h1>
            <p className="text-gray-600 text-lg mb-4">{currentProject.description}</p>
            <div className="flex items-center space-x-4 text-sm text-gray-500">
              <span>Organization: {currentProject.organization}</span>
              <span>Created: {formatDateByPreference(currentProject.created_at, settings.dateFormat, { timeZone: settings.timezone })}</span>
              <span className={`px-2 py-1 rounded-full text-xs ${
                currentProject.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
              }`}>
                {currentProject.status}
              </span>
            </div>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => navigate(`/projects/${projectId}/schedules`)}
              className="px-4 py-2 bg-nb-accent-primary hover:bg-nb-accent-blue text-white rounded-md transition-colors"
            >
              Manage Schedules
            </button>
            <button
              onClick={() => navigate(`/projects/${projectId}/reports`)}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
            >
              View Reports
            </button>
          </div>
        </div>
      </div>

      {/* Project statistics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Schedules</p>
              <p className="text-2xl font-bold text-gray-900">{totalSchedules}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Current Schedules</p>
              <p className="text-2xl font-bold text-gray-900">{currentSchedules}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Baseline Schedules</p>
              <p className="text-2xl font-bold text-gray-900">{baselineSchedules}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-orange-100 rounded-lg">
              <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Activities</p>
              <p className="text-2xl font-bold text-gray-900">{realTotalActivities}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Real Project Data from Backend */}
      {projectSummary && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-red-100 rounded-lg">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Critical Activities</p>
                <p className="text-2xl font-bold text-gray-900">{projectSummary.critical_activities || 0}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Resources</p>
                <p className="text-2xl font-bold text-gray-900">{projectSummary.resources || 0}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Progress</p>
                <p className="text-2xl font-bold text-gray-900">{realProgress}%</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Budget Used</p>
                <p className="text-2xl font-bold text-gray-900">{realBudget > 0 ? Math.round((realSpent / realBudget) * 100) : 0}%</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Project Timeline and Budget */}
      {projectSummary && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Project Timeline</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Start Date:</span>
                <span className="text-sm font-medium">{realStartDate ? formatDateByPreference(realStartDate, settings.dateFormat, { timeZone: settings.timezone }) : 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">End Date:</span>
                <span className="text-sm font-medium">{realEndDate ? formatDateByPreference(realEndDate, settings.dateFormat, { timeZone: settings.timezone }) : 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Duration:</span>
                <span className="text-sm font-medium">
                  {realStartDate && realEndDate 
                    ? Math.ceil((new Date(realEndDate).getTime() - new Date(realStartDate).getTime()) / (1000 * 60 * 60 * 24)) + ' days'
                    : 'N/A'
                  }
                </span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Budget Overview</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Total Budget:</span>
                <span className="text-sm font-medium">${realBudget ? realBudget.toLocaleString() : 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Spent:</span>
                <span className="text-sm font-medium">${realSpent ? realSpent.toLocaleString() : 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Remaining:</span>
                <span className="text-sm font-medium">${realBudget && realSpent ? (realBudget - realSpent).toLocaleString() : 'N/A'}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quick actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Schedule Management</h3>
          <div className="space-y-3">
            <button
              onClick={() => navigate(`/projects/${projectId}/schedules`)}
              className="w-full text-left p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-900">View All Schedules</p>
                  <p className="text-sm text-gray-600">Manage and compare project schedules</p>
                </div>
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </button>
            <button
              onClick={() => navigate(`/projects/${projectId}/schedules?upload=true`)}
              className="w-full text-left p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-900">Upload New Schedule</p>
                  <p className="text-sm text-gray-600">Upload XER file to create new schedule</p>
                </div>
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
              </div>
            </button>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Reports & Analysis</h3>
          <div className="space-y-3">
            <button
              onClick={() => navigate(`/projects/${projectId}/reports`)}
              className="w-full text-left p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-900">Generate Reports</p>
                  <p className="text-sm text-gray-600">Create PDF and Excel reports</p>
                </div>
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </button>
            <button
              onClick={() => navigate(`/projects/${projectId}/insights`)}
              className="w-full text-left p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-900">AI Insights</p>
                  <p className="text-sm text-gray-600">View AI-powered schedule analysis</p>
                </div>
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </button>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Project Settings</h3>
          <div className="space-y-3">
            <button
              onClick={() => navigate(`/projects/${projectId}/settings`)}
              className="w-full text-left p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-900">Project Settings</p>
                  <p className="text-sm text-gray-600">Manage project configuration</p>
                </div>
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </button>
            <button
              onClick={() => navigate(`/projects/${projectId}/team`)}
              className="w-full text-left p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-900">Team Management</p>
                  <p className="text-sm text-gray-600">Manage project team members</p>
                </div>
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Recent schedules */}
      {currentProject.schedules && currentProject.schedules.length > 0 && (
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Recent Schedules</h3>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {currentProject.schedules.slice(0, 5).map((schedule) => (
                <div key={schedule.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                  <div>
                    <h4 className="font-medium text-gray-900">{schedule.name}</h4>
                    <p className="text-sm text-gray-600">{schedule.description}</p>
                    <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
                      <span>{schedule.activity_count || 0} activities</span>
                      <span>Duration: {schedule.duration || 0} days</span>
                      <span>Created: {formatDateByPreference(schedule.created_at, settings.dateFormat, { timeZone: settings.timezone })}</span>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      schedule.status === 'Current' ? 'bg-green-100 text-green-800' :
                      schedule.status === 'Baseline' ? 'bg-blue-100 text-blue-800' :
                      schedule.status === 'Historical' ? 'bg-gray-100 text-gray-800' :
                      'bg-purple-100 text-purple-800'
                    }`}>
                      {schedule.status}
                    </span>
                    <button
                      onClick={() => navigate(`/projects/${projectId}/schedules/${schedule.id}`)}
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                      View Details
                    </button>
                  </div>
                </div>
              ))}
            </div>
            {currentProject.schedules.length > 5 && (
              <div className="mt-4 text-center">
                <button
                  onClick={() => navigate(`/projects/${projectId}/schedules`)}
                  className="text-blue-600 hover:text-blue-800 font-medium"
                >
                  View All Schedules ({currentProject.schedules.length})
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectDashboard; 

